import styled from 'styled-components';
import { css } from 'styled-components/macro';
import { Divider, Dropdown } from 'antd';
import { screenMd } from 'constants/styles/breakpoints';

export const TeamsTableItem = styled.span<{ active?: boolean; reverse: boolean }>`
  display: flex;
  flex-flow: ${({ reverse }) => (reverse ? 'row-reverse' : 'row')};
  align-items: center;
  justify-content: ${({ reverse }) => (reverse ? 'flex-end' : 'flex-start')};
  height: 43px;
  border-radius: 5px;
  cursor: pointer;
  box-sizing: border-box;
  background: transparent;
  border: none;
  padding: 8px;
  &:focus-active {
    outline: none;
  }
  ${({ active, theme }) =>
    active &&
    `
		outline: ${theme.default.colors.buttons.blackGray6} 1px solid !important;
	`}
  > span {
    color: #1a2535;
    max-width: 150px;
    text-overflow: ellipsis;
    overflow: hidden;
    font-weight: 500;
    white-space: nowrap;
    padding-top: 2px;
  }
  > svg {
    min-width: 15px;
  }
`;

export const TeamsMenuContainer = styled.div<{
  withSubmit?: boolean;
  full?: boolean;
  wide?: boolean;
  itemsCount?: number;
}>`
  width: auto;
  ${({ full }) => full && 'width: 100%'};
  padding: ${({ wide }) => (wide ? '20px 0 85px 0' : '20px 5px 85px 18px')};
  background: #ffffff;
  box-shadow: 0px 30px 48px rgba(0, 0, 0, 0.2);
  position: relative;
  border-radius: 6px;
  > .teams-options-container {
    max-height: 230px;
    overflow-y: scroll;
    &--with-submit {
      ${({ itemsCount }) =>
        itemsCount == null
          ? 'max-height: 250px'
          : itemsCount > 3
          ? 'max-height: 300px'
          : `max-height: ${itemsCount * 85 + 100}px`};
      padding-bottom: 47px;
    }
    > * {
      ${({ wide }) => wide && 'padding-right: 18px; padding-left: 18px;'};
    }
  }
  ${({ withSubmit }) =>
    withSubmit &&
    `
		padding-bottom: 20px;
    ${({ itemsCount }) =>
      itemsCount == null
        ? 'max-height: 250px'
        : itemsCount > 3
        ? 'max-height: 300px'
        : `max-height: ${itemsCount * 85 + 100}px`};
	`}
  ${screenMd} {
    min-width: 350px;
    max-width: 450px;
  }
`;

export const MenuDivider = styled(Divider)`
  margin: 0;
  width: calc(100% - 10px);
  min-width: auto;
`;

export const CustomDropdown = styled(Dropdown)<{ maxWidth: string }>`
  max-width: ${({ maxWidth }) => maxWidth};
`;

export const TeamsSectionTitle = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  margin-bottom: 10px;
`;

export const TeamsMenuItem = styled.button<{ withActiveUsers?: boolean }>`
  width: calc(100% - 10px);
  height: 55px;
  display: flex;
  justify-content: start;
  align-items: center;
  cursor: pointer;
  border-radius: 5px;
  box-sizing: border-box;
  border: none;
  background: #ffffff;
  &:focus,
  &:hover {
    background: ${({ theme }) => theme.default.colors.buttons.blackGray7};
  }
  &:active {
    background: ${({ theme }) => theme.default.colors.buttons.blackGray6};
  }
  > .teams-icon {
    margin-right: 10px;
  }
  > .team-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    margin-left: 10px;
  }
  > .teams-label {
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.default.colors.buttons.blackGray1};
    max-width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  > .active-users-label {
    color: var(--light-gray-darker);
  }
  ${({ withActiveUsers }) =>
    withActiveUsers &&
    `
      width: 100%;
      padding: 12px 0px;
      padding-right: 18px;
      height: auto;
      border-radius: 0;
    `};
`;

const ctaBase = css`
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: space-between;
  background: #ffffff;
  padding: 20px;
  border-radius: 6px;
  z-index: 10;
`;

export const TeamsDropdownCTA = styled.div`
  ${ctaBase}

  > button {
    flex-grow: 1;
    &:first-of-type {
      margin-right: 10px;
    }
  }
`;

export const TeamsDropdownFooter = styled.div`
  ${ctaBase}
  display: flex;
  align-items: center;
  padding: 12px 20px 20px;
  > button {
    margin-left: auto;
    width: fit-content;
  }
`;

export const TeamsStatusMessage = styled.div`
  font-size: 12px;
  line-height: 14px;
  span {
    font-weight: 700;
    color: var(--black-dark);
  }
`;

enum sizes {
  default = '60px',
  large = '80px',
  small = '54px',
  smaller = '44px',
}

export const IconContainer = styled.div<{ color?: string; size: string }>`
  border-radius: 6px;
  background-color: ${({ color, theme }) =>
    theme.default.colors.dataVisualization[color]?.lighter || 'lightGray'};
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ size }) => sizes[size]};
  height: ${({ size }) => sizes[size]};
  > img,
  span,
  svg {
    width: ${({ size }) => sizes[size]};
    height: ${({ size }) => sizes[size]};
    border-radius: 6px;
    &.default {
      background-color: ${({ theme }) => theme.default.colors.dataVisualization['purple'].lighter};
    }
    &.remove-team {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background: #fff;
      border: 1px solid ${({ theme }) => theme.default.colors.dataVisualization['purple'].lighter};
    }
  }
  > svg {
    stroke: ${({ color, theme }) => theme.default.colors.dataVisualization[color]?.base} !important;
    fill: ${({ color, theme }) => theme.default.colors.dataVisualization[color]?.base} !important;
    stroke-width: 0px;
  }
`;

export const UserRowTeamsContainer = styled.div`
  display: grid;
`;
